/**
 * Projects Service - Handles project portfolio management operations
 * Provides CRUD operations for construction projects
 */

import { safeFrom, safeAuth, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notFoundError,
  notConfiguredError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import type { 
  Project, 
  ProjectFormValues,
  ProjectCategory,
  GetProjectsOptions
} from '@/types/projects';

export const projectsService = {
  /**
   * Get all projects (published only for public, all for admins)
   * @param options - Query options including category filter
   * @returns Array of projects
   */
  async getProjects(options: GetProjectsOptions = {}): Promise<ServiceResponse<Project[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const { category, isAdmin = false, limit, offset } = options;
      
      let query = safeFrom('projects')
        .select('*')
        .order('sort_order', { ascending: false })
        .order('created_at', { ascending: false });

      // Filter by category if provided
      if (category) {
        query = query.eq('category', category) as any;
      }

      // Filter by published status for public
      if (!isAdmin) {
        query = query.eq('is_published', true) as any;
      }

      // Apply pagination
      if (limit) {
        query = query.limit(limit) as any;
      }
      if (offset !== undefined && limit) {
        query = query.range(offset, offset + limit - 1) as any;
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Transform image_urls from JSONB to array
      const projects = (data || []).map((project: any) => ({
        ...project,
        image_urls: Array.isArray(project.image_urls) 
          ? project.image_urls 
          : (project.image_urls ? JSON.parse(JSON.stringify(project.image_urls)) : []),
      })) as Project[];

      return successResponse(projects);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'projectsService.getProjects', { options }));
    }
  },

  /**
   * Get a single project by ID
   * @param id - Project ID
   * @param isAdmin - Whether the request is from an admin user
   * @returns Project or null
   */
  async getProject(id: number, { isAdmin = false }: { isAdmin?: boolean } = {}): Promise<ServiceResponse<Project | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }

    try {
      let query = safeFrom('projects')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (!isAdmin) {
        query = query.eq('is_published', true) as any;
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      if (!data) {
        return successResponse(null);
      }

      // Transform image_urls from JSONB to array
      const project: Project = {
        ...data,
        image_urls: Array.isArray(data.image_urls) 
          ? data.image_urls 
          : (data.image_urls ? JSON.parse(JSON.stringify(data.image_urls)) : []),
      };

      return successResponse(project);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'projectsService.getProject', { id }));
    }
  },

  /**
   * Create a new project (admin only)
   * @param projectData - Project data
   * @returns Created project
   */
  async createProject(projectData: ProjectFormValues): Promise<ServiceResponse<Project>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const user = await safeAuth.getUser();
      if (!user?.user) {
        throw new Error('Authentication required');
      }

      // Convert image_urls array to JSONB
      const insertData: any = {
        ...projectData,
        image_urls: projectData.image_urls ? JSON.stringify(projectData.image_urls) : '[]',
      };

      const { data, error } = await safeFrom('projects')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const project: Project = {
        ...data,
        image_urls: Array.isArray(data.image_urls) 
          ? data.image_urls 
          : (data.image_urls ? JSON.parse(JSON.stringify(data.image_urls)) : []),
      };

      // Audit log
      await auditContent('project', 'create', project.id.toString(), user.user.id, {
        title: project.title,
      });

      return successResponse(project);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'projectsService.createProject', { projectData }));
    }
  },

  /**
   * Update an existing project (admin only)
   * @param id - Project ID
   * @param projectData - Updated project data
   * @returns Updated project
   */
  async updateProject(id: number, projectData: Partial<ProjectFormValues>): Promise<ServiceResponse<Project>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const user = await safeAuth.getUser();
      if (!user?.user) {
        throw new Error('Authentication required');
      }

      // Convert image_urls array to JSONB if provided
      const updateData: any = { ...projectData };
      if (projectData.image_urls !== undefined) {
        updateData.image_urls = JSON.stringify(projectData.image_urls);
      }

      const { data, error } = await safeFrom('projects')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        return errorResponse(notFoundError('Project'));
      }

      const project: Project = {
        ...data,
        image_urls: Array.isArray(data.image_urls) 
          ? data.image_urls 
          : (data.image_urls ? JSON.parse(JSON.stringify(data.image_urls)) : []),
      };

      // Audit log
      await auditContent('project', 'update', id.toString(), user.user.id, {
        title: project.title,
      });

      return successResponse(project);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'projectsService.updateProject', { id, projectData }));
    }
  },

  /**
   * Delete a project (admin only)
   * @param id - Project ID
   * @returns Success boolean
   */
  async deleteProject(id: number): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const user = await safeAuth.getUser();
      if (!user?.user) {
        throw new Error('Authentication required');
      }

      // Get project title for audit log
      const { data: project } = await safeFrom('projects')
        .select('title')
        .eq('id', id)
        .single();

      const { error } = await safeFrom('projects')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Audit log
      await auditContent('project', 'delete', id.toString(), user.user.id, {
        title: project?.title || 'Unknown',
      });

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'projectsService.deleteProject', { id }));
    }
  },
};
